import React, { useState, useEffect } from 'react';
import { Modal, ModalBody } from 'reactstrap';
import styles from './styles.module.scss';
import 'bootstrap/dist/css/bootstrap.min.css';
import { IAddressData } from '../../ts/interfaces/Order/interface';
import { toast } from 'react-toastify';

interface ViaCEPResponse {
    cep: string;
    logradouro: string;
    complemento: string;
    bairro: string;
    localidade: string;
    uf: string;
    erro?: boolean;
}

interface ModalEditAddressProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (addressData: IAddressData) => void;
    addressType: 'shipping' | 'billing';
    initialData?: IAddressData;
    loading?: boolean;
}

const ModalEditAddress: React.FC<ModalEditAddressProps> = ({
    isOpen,
    onClose,
    onSave,
    addressType,
    initialData,
    loading = false
}) => {
    const [formData, setFormData] = useState<IAddressData>({
        street: '',
        number: '',
        neighborhood: '',
        complement: '',
        city: '',
        state: '',
        zipcode: ''
    });

    const [errors, setErrors] = useState<Partial<IAddressData>>({});
    const [isLoadingCEP, setIsLoadingCEP] = useState<boolean>(false);
    const [isValidatingCEP, setIsValidatingCEP] = useState<boolean>(false);

    useEffect(() => {
        if (initialData) {
            setFormData(initialData);
        } else {
            setFormData({
                street: '',
                number: '',
                neighborhood: '',
                complement: '',
                city: '',
                state: '',
                zipcode: ''
            });
        }
        setErrors({});
    }, [initialData, isOpen]);

    const validateCEP = (cep: string): boolean => {
        const cepRegex = /^\d{5}-?\d{3}$/;
        return cepRegex.test(cep);
    };

    const fetchCEPData = async (cep: string) => {
        try {
            setIsLoadingCEP(true);
            const cleanCEP = cep.replace(/\D/g, '');
            
            if (cleanCEP.length !== 8) {
                toast.error('CEP deve ter 8 dígitos');
                return;
            }

            const response = await fetch(`https://viacep.com.br/ws/${cleanCEP}/json/`);
            const data: ViaCEPResponse = await response.json();

            if (data.erro) {
                toast.error('CEP não encontrado');
                return;
            }

            // Preencher os campos automaticamente
            setFormData(prev => ({
                ...prev,
                street: data.logradouro || '',
                neighborhood: data.bairro || '',
                city: data.localidade || '',
                state: data.uf || '',
                complement: data.complemento || prev.complement // Manter o complemento existente se houver
            }));

            toast.success('Endereço encontrado e preenchido automaticamente');
        } catch (error) {
            console.error('Erro ao buscar CEP:', error);
            toast.error('Erro ao buscar CEP. Tente novamente.');
        } finally {
            setIsLoadingCEP(false);
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Partial<IAddressData> = {};

        if (!formData.street.trim()) {
            newErrors.street = 'Rua é obrigatória';
        }

        if (!formData.number.trim()) {
            newErrors.number = 'Número é obrigatório';
        }

        if (!formData.neighborhood.trim()) {
            newErrors.neighborhood = 'Bairro é obrigatório';
        }

        if (!formData.city.trim()) {
            newErrors.city = 'Cidade é obrigatória';
        }

        if (!formData.state.trim()) {
            newErrors.state = 'Estado é obrigatório';
        }

        if (!formData.zipcode.trim()) {
            newErrors.zipcode = 'CEP é obrigatório';
        } else if (!validateCEP(formData.zipcode)) {
            newErrors.zipcode = 'CEP inválido (formato: 00000-000)';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateCEPOnSave = async (): Promise<boolean> => {
        if (!formData.zipcode.trim()) {
            toast.error('CEP é obrigatório');
            return false;
        }

        if (!validateCEP(formData.zipcode)) {
            toast.error('CEP inválido (formato: 00000-000)');
            return false;
        }

        try {
            setIsValidatingCEP(true);
            const cleanCEP = formData.zipcode.replace(/\D/g, '');
            const response = await fetch(`https://viacep.com.br/ws/${cleanCEP}/json/`);
            const data: ViaCEPResponse = await response.json();

            if (data.erro) {
                toast.error('CEP não encontrado. Verifique se o CEP está correto.');
                return false;
            }

            // Verificar se os dados do CEP correspondem aos campos preenchidos
            const cepData = {
                street: data.logradouro || '',
                neighborhood: data.bairro || '',
                city: data.localidade || '',
                state: data.uf || ''
            };

            const isStreetMatch = cepData.street.toLowerCase().includes(formData.street.toLowerCase()) || 
                                 formData.street.toLowerCase().includes(cepData.street.toLowerCase());
            const isNeighborhoodMatch = cepData.neighborhood.toLowerCase() === formData.neighborhood.toLowerCase();
            const isCityMatch = cepData.city.toLowerCase() === formData.city.toLowerCase();
            const isStateMatch = cepData.state.toLowerCase() === formData.state.toLowerCase();

            if (!isStreetMatch || !isNeighborhoodMatch || !isCityMatch || !isStateMatch) {
                toast.error('Os dados do endereço não correspondem ao CEP informado. Verifique os dados.');
                return false;
            }

            return true;
        } catch (error) {
            console.error('Erro ao validar CEP:', error);
            toast.error('Erro ao validar CEP. Tente novamente.');
            return false;
        } finally {
            setIsValidatingCEP(false);
        }
    };

    const handleInputChange = (field: keyof IAddressData, value: string) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Limpar erro do campo quando o usuário começar a digitar
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: undefined
            }));
        }
    };

    const handleZipcodeChange = (value: string) => {
        // Formatar CEP automaticamente
        let formattedZipcode = value.replace(/\D/g, '');
        if (formattedZipcode.length > 5) {
            formattedZipcode = formattedZipcode.substring(0, 5) + '-' + formattedZipcode.substring(5, 8);
        }
        handleInputChange('zipcode', formattedZipcode);

        // Buscar CEP automaticamente quando tiver 8 dígitos
        const cleanCEP = formattedZipcode.replace(/\D/g, '');
        if (cleanCEP.length === 8) {
            fetchCEPData(formattedZipcode);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        // Primeiro valida os campos obrigatórios
        if (!validateForm()) {
            return;
        }

        // Depois valida o CEP com a API
        const isCEPValid = await validateCEPOnSave();
        if (!isCEPValid) {
            return;
        }

        // Se tudo estiver correto, salva os dados
        onSave(formData);
    };

    const getTitle = () => {
        return addressType === 'shipping' 
            ? 'Editar Endereço de Entrega' 
            : 'Editar Endereço de Cobrança';
    };

    return (
        <Modal isOpen={isOpen} toggle={onClose} className={styles.modalContent}>
            <ModalBody className={styles.modalBody}>
                <div className={styles.header}>
                    <h3>{getTitle()}</h3>
                    <span className={styles.close} onClick={onClose}>&times;</span>
                </div>
                
                <form onSubmit={handleSubmit} className={styles.form}>
                    <div className={styles.formGroup}>
                        <label htmlFor="zipcode">CEP *</label>
                        <div className={styles.inputContainer}>
                            <input
                                type="text"
                                id="zipcode"
                                value={formData.zipcode}
                                onChange={(e) => handleZipcodeChange(e.target.value)}
                                placeholder="00000-000"
                                className={errors.zipcode ? styles.error : ''}
                                maxLength={9}
                                disabled={isLoadingCEP}
                            />
                            {isLoadingCEP && (
                                <div className={styles.loadingSpinner}>
                                    <div className={styles.spinner}></div>
                                </div>
                            )}
                        </div>
                        {errors.zipcode && <span className={styles.errorMessage}>{errors.zipcode}</span>}
                    </div>

                    <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                            <label htmlFor="street">Rua *</label>
                            <input
                                type="text"
                                id="street"
                                value={formData.street}
                                onChange={(e) => handleInputChange('street', e.target.value)}
                                placeholder="Nome da rua"
                                className={errors.street ? styles.error : ''}
                            />
                            {errors.street && <span className={styles.errorMessage}>{errors.street}</span>}
                        </div>

                        <div className={styles.formGroup}>
                            <label htmlFor="number">Número *</label>
                            <input
                                type="text"
                                id="number"
                                value={formData.number}
                                onChange={(e) => handleInputChange('number', e.target.value)}
                                placeholder="Número"
                                className={errors.number ? styles.error : ''}
                            />
                            {errors.number && <span className={styles.errorMessage}>{errors.number}</span>}
                        </div>
                    </div>

                    <div className={styles.formGroup}>
                        <label htmlFor="neighborhood">Bairro *</label>
                        <input
                            type="text"
                            id="neighborhood"
                            value={formData.neighborhood}
                            onChange={(e) => handleInputChange('neighborhood', e.target.value)}
                            placeholder="Nome do bairro"
                            className={errors.neighborhood ? styles.error : ''}
                        />
                        {errors.neighborhood && <span className={styles.errorMessage}>{errors.neighborhood}</span>}
                    </div>

                    <div className={styles.formGroup}>
                        <label htmlFor="complement">Complemento</label>
                        <input
                            type="text"
                            id="complement"
                            value={formData.complement}
                            onChange={(e) => handleInputChange('complement', e.target.value)}
                            placeholder="Apartamento, casa, etc."
                        />
                    </div>

                    <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                            <label htmlFor="city">Cidade *</label>
                            <input
                                type="text"
                                id="city"
                                value={formData.city}
                                onChange={(e) => handleInputChange('city', e.target.value)}
                                placeholder="Nome da cidade"
                                className={errors.city ? styles.error : ''}
                            />
                            {errors.city && <span className={styles.errorMessage}>{errors.city}</span>}
                        </div>

                        <div className={styles.formGroup}>
                            <label htmlFor="state">Estado *</label>
                            <input
                                type="text"
                                id="state"
                                value={formData.state}
                                onChange={(e) => handleInputChange('state', e.target.value)}
                                placeholder="UF"
                                className={errors.state ? styles.error : ''}
                                maxLength={2}
                            />
                            {errors.state && <span className={styles.errorMessage}>{errors.state}</span>}
                        </div>
                    </div>

                    <div className={styles.buttons}>
                        <button type="button" onClick={onClose} disabled={loading}>
                            Cancelar
                        </button>
                        <button type="submit" disabled={loading || isValidatingCEP}>
                            {loading ? 'Salvando...' : isValidatingCEP ? 'Validando CEP...' : 'Salvar'}
                        </button>
                    </div>
                </form>
            </ModalBody>
        </Modal>
    );
};

export default ModalEditAddress;
